import React, { useEffect, useRef, useMemo } from 'react';
import { arr } from "@/features/shared/lib/utils";
import noUiSlider from 'nouislider';
import '@/styles/nouislider.min.css';

export function FacetBlock({ title, items, showAll = false, andQuery = false, facetKey, selected = [], onToggle, onRefine, onMore, conjunction = null, conjunctionValue = null, onConjunctionChange }) {
  const list = arr(items);
  // Flatten nested grouped facet structure like [{name: 'Scopus', result:[{title,count}...]}]
  const flatList = (() => {
    // New structure: if items is an object with 'result' array
    if (items && typeof items === 'object' && !Array.isArray(items) && Array.isArray(items.result)) {
      return items.result.map(r => ({
        title: r?.name ?? '',
        id: r?.id ?? r?.name ?? '',
        label: r?.name ?? '',
        count: r?.count ?? 0,
      }));
    }

    if (list.length && list[0] && typeof list[0] === 'object' && 'name' in list[0] && Array.isArray(list[0].result)) {
      try {
        return list.flatMap(group => {
          const gname = group?.name ?? '';
          const arrRes = Array.isArray(group?.result) ? group.result : [];
          return arrRes.map(r => ({
            title: r?.title ?? r?.name ?? '',
            id: r?.id ?? r?.title ?? r?.name ?? '',
            label: [gname, r?.title ?? r?.name].filter(Boolean).join(' '),
            count: r?.count ?? 0,
          }));
        });
      } catch (e) {
        return [];
      }
    }
    return list.map(it => ({
      ...it,
      title: it?.title ?? it?.name ?? '',
      id: it?.id ?? it?.title ?? it?.name ?? '',
    }));
  })();
  const filteredList = flatList.filter(it => String(it?.title ?? '').trim() !== '');
  const displayList = showAll ? filteredList : filteredList.slice(0, 5);
  const hasMore = !showAll && filteredList.length > 5;

  return (
    <div className="filter-item mb-9">
      {title && <h4 className="mb-[18px] text-[14px] font-bold">{title}</h4>}

      <ul className="flex flex-col gap-1">
        {displayList.map((it, idx) => {
          const display = (it.label ?? it.title ?? '');
          const value = (it.id ?? it.title ?? it.label ?? '');
          const checked = Array.isArray(selected) && selected.includes(value);
          return (
            <li key={`${value || 'item'}-${idx}`} className="w-full">
              <label className="checkbox-wrap group inline-flex items-start cursor-pointer select-none">
                {/* 체크박스(복수 선택 가능) */}
                <input
                  type="checkbox"
                  className="peer hidden"
                  checked={checked}
                  onChange={() => {
                    if (typeof onToggle === 'function' && facetKey) onToggle(facetKey, value);
                  }}
                />
                <span className="w-4 h-4 shrink-0 flex items-center justify-center mt-[3px] border border-[#B4BCCC] rounded-sm peer-checked:border-[#4B4DFF] peer-checked:bg-[#4B4DFF] peer-checked:bg-[url('/images/checked.png')] bg-no-repeat bg-size-[cover] bg-position-[center] group-hover:border-[#4B4DFF] transition-colors duration-200" />
                <span className="ml-[10px] text-[14px] text-[#6C7687]">
                  {display || '(제목 없음)'} (<span>{it.count.toLocaleString() ?? 0}</span>)
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {/* More 버튼 (hasMore가 true일 때만 표시) */}
      {hasMore && (
        <button
          onClick={() => {
            if (typeof onMore === 'function') {
              onMore({
                facetKey,
                title,
                items,
                conjunction,
                conjunctionValue,
                onConjunctionChange
              });
            }
          }}
          className="btn-filter-more w-[calc(50%-8px)] flex items-center justify-center gap-[6px] py-[8px] mt-[10px] mb-[10px] text-[14px] font-semibold border border-[#B4BCCC] rounded-md cursor-pointer lg:w-auto lg:py-[2px] lg:border-none lg:px-[10px] lg:bg-[#F3F5F9] lg:text-[13px] lg:text-[#8D9AAF] lg:gap-[2px]"
        >
          <img src={`${rmVars.themeUrl}images/more-ico-bk.png`} className="w-[14px] lg:hidden" alt="more" />
          <img src={`${rmVars.themeUrl}images/more-ico-gr.png`} className="w-[14px] hidden lg:block" alt="more" />
          More
        </button>
      )}

      {/* AND 조건 토글 (conjunction이 'and'인 경우에만 표시, Refine 버튼 위) */}
      {conjunction === 'and' && (
        <div className="and flex justify-between items-center mt-[10px] mb-[10px] px-[12px] py-[8px] bg-[#F3F5F9] rounded-md text-[#8D9AAF] text-[14px]">
          <span>And condition search</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={conjunctionValue === 'and'}
              onChange={() => {
                if (typeof onConjunctionChange === 'function') {
                  onConjunctionChange(facetKey, conjunctionValue === 'and' ? null : 'and');
                }
              }}
            />
            <div className="w-[25px] h-[15px] bg-[#B4BCCC] rounded-full peer peer-checked:bg-[#4B4DFF] transition-colors duration-200"></div>
            <div className="absolute left-[2px] top-[2px] w-[11px] h-[11px] bg-white rounded-full transition-transform duration-200 peer-checked:translate-x-[10px]"></div>
          </label>
        </div>
      )}

      {/* Refine 버튼 */}
      <button
        onClick={() => typeof onRefine === 'function' && onRefine()}
        className={`w-full flex items-center justify-center gap-[6px] py-[8px] text-[14px] font-semibold border border-[#B4BCCC] rounded-md cursor-pointer ${hasMore ? 'mt-[10px] lg:mt-1' : 'mt-[8px]'}`}
      >
        <img src={`${rmVars.themeUrl}images/search-ico-333.png`} className="w-[12px]" alt="search" />
        Refine
      </button>
    </div>
  );
}


export function RangeBlock({
  title,
  facetKey,
  facetData,
  filters = {},
  onRefine
}) {
  const sliderRef = useRef(null);
  const inputMinRef = useRef(null);
  const inputMaxRef = useRef(null);
  const pathRef = useRef(null);
  const sliderInstanceRef = useRef(null);

  // facetData에서 min, max, array 추출
  const rangeData = facetData?.result || {};
  const minValue = rangeData.min ?? 0;
  const maxValue = rangeData.max ?? 1000;
  const arrayData = Array.isArray(rangeData.array) ? rangeData.array : [];

  // 현재 필터에서 min/max 값 가져오기
  const minParam = `min_${facetKey}`;
  const maxParam = `max_${facetKey}`;
  const currentMin = filters[minParam] !== undefined ? Number(filters[minParam]) : minValue;
  const currentMax = filters[maxParam] !== undefined ? Number(filters[maxParam]) : maxValue;

  // SVG path 생성 (array 데이터 기반)
  const generateSVGPath = useMemo(() => {
    if (!arrayData.length) return 'M0,60 Q100,10 200,40 T400,60 L400,80 L0,80 Z';

    const width = 400;
    const height = 80;
    const padding = 20;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    const maxCount = Math.max(...arrayData.map(item => item.count || 0));
    const minCount = Math.min(...arrayData.map(item => item.count || 0));
    const countRange = maxCount - minCount || 1;

    // value 범위에 맞춰 포인트 생성
    const points = arrayData.map(item => {
      const value = Number(item.value || 0);
      const count = Number(item.count || 0);
      const normalizedValue = (value - minValue) / (maxValue - minValue || 1);
      const x = padding + normalizedValue * chartWidth;
      const normalizedCount = countRange > 0 ? (count - minCount) / countRange : 0;
      const y = height - padding - normalizedCount * chartHeight;
      return { x, y, value, count };
    });

    if (points.length === 0) return 'M0,60 Q100,10 200,40 T400,60 L400,80 L0,80 Z';

    // 스무스 커브 경로 생성
    let path = `M${points[0].x},${points[0].y}`;

    if (points.length === 1) {
      path += ` L${points[0].x},${height} L${points[0].x},${height} Z`;
    } else {
      for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        const next = points[i + 1] || curr;

        const cp1x = prev.x + (curr.x - prev.x) / 3;
        const cp1y = prev.y;
        const cp2x = curr.x - (next.x - curr.x) / 3;
        const cp2y = curr.y;

        path += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
      }

      // 하단을 닫기
      path += ` L${points[points.length - 1].x},${height} L${points[0].x},${height} Z`;
    }

    return path;
  }, [arrayData, minValue, maxValue]);

  useEffect(() => {
    if (!sliderRef.current) return;
    const el = sliderRef.current;

    if (sliderInstanceRef.current) {
      try {
        sliderInstanceRef.current.destroy();
      } catch (e) {
      }
    }

    const slider = noUiSlider.create(el, {
      start: [currentMin, currentMax],
      connect: true,
      range: { min: minValue, max: maxValue },
      step: (maxValue - minValue) / 1000, // 적절한 step 계산
    });

    sliderInstanceRef.current = slider;

    // SVG path 설정
    if (pathRef.current && generateSVGPath) {
      pathRef.current.setAttribute('d', generateSVGPath);
    }

    const onUpdate = (values) => {
      const v0 = Number(values[0]);
      const v1 = Number(values[1]);
      if (inputMinRef.current) inputMinRef.current.value = Math.round(v0 * 100) / 100;
      if (inputMaxRef.current) inputMaxRef.current.value = Math.round(v1 * 100) / 100;
    };

    slider.on('update', onUpdate);

    // 초기값 설정
    if (inputMinRef.current) inputMinRef.current.value = Math.round(currentMin * 100) / 100;
    if (inputMaxRef.current) inputMaxRef.current.value = Math.round(currentMax * 100) / 100;

    const onMinChange = () => {
      const v = Number(inputMinRef.current?.value ?? minValue);
      slider.set([Math.max(minValue, Math.min(v, maxValue)), null]);
    };

    const onMaxChange = () => {
      const v = Number(inputMaxRef.current?.value ?? maxValue);
      slider.set([null, Math.max(minValue, Math.min(v, maxValue))]);
    };

    inputMinRef.current?.addEventListener('change', onMinChange);
    inputMaxRef.current?.addEventListener('change', onMaxChange);

    return () => {
      try {
        slider.off('update', onUpdate);
      } catch (e) {
      }
      inputMinRef.current?.removeEventListener('change', onMinChange);
      inputMaxRef.current?.removeEventListener('change', onMaxChange);
      try {
        slider.destroy();
      } catch (e) {
      }
    };
  }, [minValue, maxValue, currentMin, currentMax, generateSVGPath]);

  const handleRefine = () => {
    if (!onRefine || !sliderInstanceRef.current) return;
    const values = sliderInstanceRef.current.get();
    const min = Number(values[0]);
    const max = Number(values[1]);
    onRefine(facetKey, min, max);
  };

  return (
    <div className="filter-item mb-9">
      {title && <h4 className="mb-[18px] text-[14px] font-bold">{title}</h4>}

      <div className="relative w-full mx-auto mt-20">
        <svg
          viewBox="0 0 400 80"
          className="absolute top-[-80px] left-0 w-full h-[80px] z-0"
        >
          <path
            ref={pathRef}
            d={generateSVGPath}
            fill="#4B4DFF1A"
            stroke="none"
          />
        </svg>

        <div ref={sliderRef} className="relative z-10"></div>

        <div className="flex items-center gap-2 mt-6">
          <input
            ref={inputMinRef}
            type="number"
            step="0.01"
            min={minValue}
            max={maxValue}
            value={currentMin}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (!isNaN(v) && v >= minValue && v <= maxValue && sliderInstanceRef.current) {
                sliderInstanceRef.current.set([v, null]);
              }
            }}
            className="w-full h-[36px] px-2 border border-[#E4E8F1] rounded-lg text-[13px] text-[#333]"
          />
          <span className="text-[#8D9AAF]">~</span>
          <input
            ref={inputMaxRef}
            type="number"
            step="0.01"
            min={minValue}
            max={maxValue}
            value={currentMax}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (!isNaN(v) && v >= minValue && v <= maxValue && sliderInstanceRef.current) {
                sliderInstanceRef.current.set([null, v]);
              }
            }}
            className="w-full h-[36px] px-2 border border-[#E4E8F1] rounded-lg text-[13px] text-[#333]"
          />
        </div>
      </div>

      <button
        onClick={handleRefine}
        className="w-full flex items-center justify-center gap-[6px] py-[8px] mt-[8px] text-[14px] font-semibold border border-[#B4BCCC] rounded-md cursor-pointer"
      >
        <img src={`${rmVars.themeUrl}images/search-ico-333.png`} className="w-[12px]" alt="search" />
        Refine
      </button>
    </div>
  );
}